import * as React from "react";
import { CalendarIcon, X, Clock } from "lucide-react";
import { format, parse, isValid, addDays, addYears, startOfMonth, addMonths } from "date-fns";
import { it } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

/**
 * DateTimePicker localizzato (IT · DD/MM/YYYY HH:mm) drop-in per `<input type="datetime-local">`.
 *
 * value/onChange usano la stessa stringa di datetime-local: "YYYY-MM-DDTHH:mm".
 */
export function DateTimePicker({
  value,
  onChange,
  min,
  max,
  disabled,
  placeholder = "gg/mm/aaaa hh:mm",
  className,
  clearable = true,
  fromYear,
  toYear,
  minuteStep = 5,
  quickTimes = ["09:00", "10:30", "12:00", "14:30", "16:00", "17:30", "18:30"],
  ...rest
}) {
  const [open, setOpen] = React.useState(false);

  const currentYear = new Date().getFullYear();
  fromYear = fromYear ?? 1990;
  toYear = toYear ?? (currentYear + 25);

  const parts = React.useMemo(() => {
    if (!value) return { datePart: "", timePart: "" };
    const s = String(value);
    const [dp, tpRaw = ""] = s.split("T");
    const tp = tpRaw.slice(0, 5);
    return { datePart: dp, timePart: tp };
  }, [value]);

  const parsedDate = React.useMemo(() => {
    if (!parts.datePart) return null;
    const d = parse(parts.datePart, "yyyy-MM-dd", new Date());
    return isValid(d) ? d : null;
  }, [parts.datePart]);

  const minDate = React.useMemo(() => min ? parse(String(min).slice(0, 10), "yyyy-MM-dd", new Date()) : undefined, [min]);
  const maxDate = React.useMemo(() => max ? parse(String(max).slice(0, 10), "yyyy-MM-dd", new Date()) : undefined, [max]);

  const emit = (nextDate, nextTime) => {
    if (typeof onChange !== "function") return;
    if (!nextDate) { onChange({ target: { value: "" } }); return; }
    const t = nextTime && /^\d{2}:\d{2}$/.test(nextTime) ? nextTime : "09:00";
    onChange({ target: { value: `${nextDate}T${t}` } });
  };

  const handleSelectDay = (day) => {
    if (!day) { emit("", ""); setOpen(false); return; }
    const iso = format(day, "yyyy-MM-dd");
    emit(iso, parts.timePart || "09:00");
  };

  const handleTimeChange = (e) => {
    const t = e.target.value;
    if (!parts.datePart) {
      const today = format(new Date(), "yyyy-MM-dd");
      emit(today, t);
    } else {
      emit(parts.datePart, t);
    }
  };

  const handleQuickTime = (t) => {
    if (!parts.datePart) {
      const today = format(new Date(), "yyyy-MM-dd");
      emit(today, t);
    } else {
      emit(parts.datePart, t);
    }
  };

  const handleClear = (e) => {
    e.stopPropagation();
    emit("", "");
  };

  const displayLabel = parsedDate
    ? `${format(parsedDate, "dd/MM/yyyy", { locale: it })}${parts.timePart ? ` · ${parts.timePart}` : ""}`
    : "";

  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      <Popover open={open && !disabled} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            className={cn(
              "flex h-10 w-full items-center justify-between rounded-md border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm ring-offset-background transition-colors",
              "hover:border-blue-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1",
              "disabled:cursor-not-allowed disabled:opacity-50",
              !displayLabel && "text-slate-400",
              displayLabel && "text-slate-900 font-medium",
            )}
            {...rest}
          >
            <span className="truncate tabular-nums">{displayLabel || placeholder}</span>
            <span className="ml-2 flex items-center gap-1 shrink-0">
              {clearable && displayLabel && !disabled && (
                <span
                  role="button"
                  tabIndex={-1}
                  onClick={handleClear}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") handleClear(e); }}
                  className="p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-rose-600 transition-colors"
                  title="Svuota"
                  data-testid="datetimepicker-clear"
                >
                  <X className="h-3.5 w-3.5" />
                </span>
              )}
              <CalendarIcon className="h-4 w-4 text-blue-500" />
            </span>
          </button>
        </PopoverTrigger>
        <PopoverContent
          className="w-auto p-0 rounded-lg border-slate-200 shadow-2xl overflow-hidden max-h-[85vh] overflow-y-auto"
          align="start"
          sideOffset={4}
        >
          {/* Header preview data selezionata */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-500 text-white px-3 py-2">
            <p className="text-[9px] uppercase tracking-widest opacity-80">Data selezionata</p>
            <p className="text-xs font-semibold tabular-nums leading-tight">
              {parsedDate
                ? format(parsedDate, "EEE d MMM yyyy", { locale: it }).replace(/^\w/, (c) => c.toUpperCase())
                : "— Nessuna data —"}
              {parts.timePart && (
                <span className="ml-1.5 text-blue-100">· {parts.timePart}</span>
              )}
            </p>
          </div>

          <Calendar
            mode="single"
            locale={it}
            captionLayout="dropdown-buttons"
            fromYear={fromYear}
            toYear={toYear}
            selected={parsedDate || undefined}
            defaultMonth={parsedDate || undefined}
            onSelect={handleSelectDay}
            disabled={(date) => {
              if (minDate && date < minDate) return true;
              if (maxDate && date > maxDate) return true;
              return false;
            }}
            initialFocus
            className="p-2"
            classNames={{
              months: "flex flex-col",
              month: "space-y-1.5",
              caption: "flex justify-center pt-1 relative items-center mb-1",
              caption_label: "hidden",
              vhidden: "sr-only",
              caption_dropdowns: "flex items-center gap-1",
              dropdown: "h-7 rounded-md border border-slate-200 bg-white px-2 pr-6 text-xs font-semibold text-slate-700 hover:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-300 cursor-pointer transition-colors capitalize",
              dropdown_month: "text-xs",
              dropdown_year: "text-xs",
              nav: "space-x-1 flex items-center",
              nav_button: "h-6 w-6 bg-transparent p-0 opacity-70 hover:opacity-100 hover:bg-slate-100 rounded-md transition-colors inline-flex items-center justify-center",
              nav_button_previous: "absolute left-1",
              nav_button_next: "absolute right-1",
              table: "w-full border-collapse",
              head_row: "flex",
              head_cell: "text-slate-500 rounded-md w-8 font-semibold text-[10px] uppercase tracking-wide",
              row: "flex w-full mt-0.5",
              cell: "relative p-0 text-center text-xs focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-transparent",
              day: "h-8 w-8 p-0 text-[12px] font-normal text-slate-700 rounded-full transition-colors hover:bg-blue-100 hover:text-blue-900 aria-selected:opacity-100 inline-flex items-center justify-center",
              day_selected: "!bg-blue-600 !text-white hover:!bg-blue-700 focus:!bg-blue-700 font-semibold shadow-sm",
              day_today: "ring-1.5 ring-blue-400 ring-offset-1 font-bold text-blue-700",
              day_outside: "text-slate-300 aria-selected:text-slate-400",
              day_disabled: "text-slate-300 opacity-40 line-through hover:bg-transparent",
              day_hidden: "invisible",
            }}
          />

          {/* Quick-time chips */}
          <div className="border-t border-slate-200 px-2.5 py-1.5 bg-slate-50/70">
            <div className="flex items-center gap-1.5 mb-1">
              <Clock className="h-3 w-3 text-blue-600" />
              <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-600">Ora rapida</span>
            </div>
            <div className="flex flex-wrap gap-1">
              {quickTimes.map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => handleQuickTime(t)}
                  className={cn(
                    "px-2 py-0.5 rounded-md border text-[11px] font-semibold tabular-nums transition-colors",
                    parts.timePart === t
                      ? "border-blue-600 bg-blue-600 text-white shadow-sm"
                      : "border-slate-200 bg-white text-slate-700 hover:border-blue-400 hover:text-blue-700",
                  )}
                  data-testid={`datetimepicker-quicktime-${t}`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>

          {/* Shortcut giorni */}
          <div className="border-t border-slate-200 px-2.5 py-1.5 bg-slate-50/70 flex flex-wrap gap-1" data-testid="datetimepicker-shortcuts">
            {[
              { label: "Oggi", get: () => new Date() },
              { label: "Domani", get: () => addDays(new Date(), 1) },
              { label: "+7 gg", get: () => addDays(new Date(), 7) },
              { label: "+1 mese", get: () => startOfMonth(addMonths(new Date(), 1)) },
              { label: "+1 anno", get: () => addYears(new Date(), 1) },
            ].map((s) => (
              <button
                key={s.label}
                type="button"
                onClick={() => handleSelectDay(s.get())}
                className="text-[10px] font-semibold px-1.5 py-0.5 rounded-md border border-slate-200 bg-white text-slate-700 hover:border-emerald-400 hover:text-emerald-700 transition-colors"
                data-testid={`datetimepicker-shortcut-${s.label.replace(/[\s.+]+/g, "-").toLowerCase()}`}
              >
                {s.label}
              </button>
            ))}
          </div>

          {/* Footer con conferma */}
          <div className="border-t border-slate-200 px-2.5 py-1.5 bg-white flex justify-end">
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-[11px] font-semibold px-3 py-1 rounded-md bg-blue-600 text-white hover:bg-blue-700 transition-colors shadow-sm"
              data-testid="datetimepicker-confirm"
            >
              Conferma
            </button>
          </div>
        </PopoverContent>
      </Popover>

      <label className="relative inline-flex items-center group w-full">
        <Clock className="h-4 w-4 text-blue-500 absolute left-2.5 pointer-events-none group-focus-within:text-blue-700" />
        <input
          type="time"
          step={minuteStep * 60}
          value={parts.timePart || ""}
          onChange={handleTimeChange}
          disabled={disabled}
          className={cn(
            "h-9 w-full rounded-md border border-slate-300 bg-white pl-8 pr-2 text-sm font-semibold tabular-nums text-slate-800 shadow-sm transition-colors",
            "hover:border-blue-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1 focus-visible:border-blue-500",
            "disabled:cursor-not-allowed disabled:opacity-50",
          )}
          data-testid="datetimepicker-time"
        />
      </label>
    </div>
  );
}

export default DateTimePicker;
